import { useSearchParams } from 'react-router-dom';
import { useExchangeXeroCode } from '../../../../../queries/use-exchange-xero-code';
import { useState, useEffect } from 'react';
import { XeroTenantConnection } from '../../../../../types/xero';
import ConnectToXero from '../../../../../components/connect-to-xero/ConnectToXero';
import { AlertV2, Button, Select, SelectOption, SimpleAlert, Spinner } from '@cimplico/components';
import { Entity } from '../../../../../types/entity';
import { useUpdateEntitiesProvider } from '../../../../../queries/use-update-entities-provider';
import { toastNotification } from '../../../../../components/toast/Toast';
import { useUserContext } from '../../../../../providers/UserProvider';
import { useIsActiveSubscription } from '../../../../../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../../../../../hooks/use-is-firm-role';

type XeroEntityConfigureProps = {
    entity: Entity;
};

function XeroEntityConfigure({ entity }: XeroEntityConfigureProps): JSX.Element {
    const exchangeXeroCodeQuery = useExchangeXeroCode();
    const [connections, setConnections] = useState<XeroTenantConnection[] | undefined>();
    const [selectedConnection, setSelectedConnection] = useState<XeroTenantConnection | undefined>();
    const [xeroUserId, setXeroUserId] = useState<string | undefined>();
    const [params, setSearchParams] = useSearchParams();
    const { isAdmin } = useIsFirmRole();
    const user = useUserContext();
    const activeSubscription = useIsActiveSubscription();

    const updateEntitiesProvider = useUpdateEntitiesProvider();

    const xeroReferWarning = user.xero?.xeroRefer && !isAdmin;

    const code = params.get('code');

    function handleConnectionChange(options: SelectOption[]): void {
        const connection = connections?.find((c) => c.tenantId === options[0]?.value);
        setSelectedConnection(connection);
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (!selectedConnection || !xeroUserId) {
            return;
        }

        updateEntitiesProvider.mutate(
            { xeroUserId, xeroEntities: [{ id: entity.id, tenantId: selectedConnection.tenantId, connectionId: selectedConnection.id }] },
            {
                onSuccess: () => {
                    toastNotification('success', `Connected ${entity.name} to ${selectedConnection.tenantName}`);
                },

                onError: (res) => {
                    const errorMessage = res.response?.data.results[0].error.fields.errorMessage;
                    toastNotification('error', 'Unable to configure xero entity connection!', errorMessage);
                },
            }
        );
    }

    useEffect(() => {
        if (code) {
            setSearchParams((prev) => {
                prev.delete('code');
                return prev;
            });

            exchangeXeroCodeQuery.mutate(code, {
                onSuccess: ({ connections, xeroUserId }) => {
                    setConnections(connections);
                    setXeroUserId(xeroUserId);
                },
            });
        }
    }, [code, exchangeXeroCodeQuery, setSearchParams]);

    useEffect(() => {
        if (connections && connections[0]) {
            setSelectedConnection(connections.find((c) => c.tenantName.toLowerCase() === entity.name.toLowerCase()) || connections[0]);
        }
    }, [connections, entity.name]);

    if (exchangeXeroCodeQuery.isLoading) {
        return (
            <div className="flex items-center gap-2 mt-4">
                <Spinner size="sm" />
                <p className="text-sm text-gray-500">Getting available connections...</p>
            </div>
        );
    }

    if (connections) {
        return (
            <form onSubmit={handleSubmit} className="max-w-[500px]">
                <AlertV2
                    className="mb-2"
                    type="warn"
                    title="Ensure you are connecting the correct entity!"
                    description="If you connect a different entity, any ongoing work that relies on the previous entity's chart of accounts will be permanently deleted during the next sync."
                />
                <Select
                    title="Available Tenants"
                    options={connections.map((c) => {
                        return {
                            value: c.tenantId,
                            label: c.tenantName,
                            isSelected: selectedConnection
                                ? selectedConnection.tenantId === c.tenantId
                                : c.tenantName.toLowerCase() === entity.name.toLowerCase(),
                        };
                    })}
                    isDisabled={!activeSubscription}
                    onChange={handleConnectionChange}
                />
                {/* FORM CANCEL + SUBMIT */}
                <div className="flex justify-end gap-4 mt-2">
                    <Button
                        size="sm"
                        type="hollow"
                        label="Cancel"
                        onClick={(): void => setConnections(undefined)}
                        disabled={!activeSubscription}
                    />
                    <Button
                        size="sm"
                        type="primary"
                        label={updateEntitiesProvider.isLoading ? 'Connecting...' : 'Connect'}
                        disabled={!activeSubscription || !selectedConnection || updateEntitiesProvider.isLoading}
                        isSubmit
                        isLoading={updateEntitiesProvider.isLoading}
                    />
                </div>
            </form>
        );
    }

    return (
        <div>
            {xeroReferWarning && (
                <div className="my-4 max-w-[450px]">
                    <SimpleAlert
                        type="warn"
                        title="Your account has been flagged as a Xero app store referral. As you are NOT the firm admin, you will not be able to connect a Xero entity. To resolve this issue, please contact support."
                    />
                </div>
            )}
            <ConnectToXero
                autoRedirect
                callbackLocation={window.location.pathname + window.location.search}
                disabled={!activeSubscription || xeroReferWarning}
            />
        </div>
    );
}
export default XeroEntityConfigure;
